import { useEffect, useMemo } from 'react'
import useSWR from 'swr'

import { useAuthToken } from '@/hooks/useAuthToken'
import { createFetcher } from '@/services/api'
import { userService } from '@/services/userService'
import type { AccessibilityPreference } from '@/types/user'

/**
 * Hook to manage global high contrast mode.
 * Syncs with user preferences and applies a `.high-contrast` class to the document root.
 */
export function useHighContrastMode() {
  const { getToken, isSignedIn } = useAuthToken()
  const fetcher = useMemo(() => createFetcher(getToken), [getToken])
  const preferencesUrl = userService.getAccessibilityPreferencesUrl()

  const { data: preference } = useSWR<AccessibilityPreference>(isSignedIn ? preferencesUrl : null, fetcher, {
    revalidateOnFocus: false,
  })

  useEffect(() => {
    const isHighContrastEnabled = preference?.high_contrast_enabled ?? false
    const root = document.documentElement

    if (isHighContrastEnabled) {
      root.classList.add('high-contrast')
    } else {
      root.classList.remove('high-contrast')
    }
  }, [preference?.high_contrast_enabled])
}
